/**
 * Service layer — Contractor Portal domain.
 *
 * Two audiences, one file (mirrors services/journey.js):
 *   - PUBLIC (the GC, no Clerk): getContractorPortal(token)
 *   - TEAM (Clerk-authed): portal-link admin via /api/contractor-admin
 *
 * The portal/token is anchored to an INDIVIDUAL construction_contracts row.
 * Components never call the endpoints directly — everything routes through here.
 */

/* ── Public read (token is the only credential) ──────────────────────────── */

// PIN-proof cache (see database/140_contractor_portal_recipients.sql + api/contractor-pin.js) —
// mirrors services/journey.js's identical cache exactly, one level down. Once a portal token's PIN
// is verified, the server issues a signed, time-limited proof instead of making the client resend
// the raw PIN on every call. Purely a convenience cache: every server endpoint that checks it
// re-validates the signature/expiry itself, so a tampered localStorage value just fails closed.
function pinProofKey(token) { return 'contractor-pin-proof:' + token }
export function getCachedPinProof(token) {
  try { return localStorage.getItem(pinProofKey(token)) || null } catch { return null }
}
export function setCachedPinProof(token, proof) {
  try { if (proof) localStorage.setItem(pinProofKey(token), proof); else localStorage.removeItem(pinProofKey(token)) } catch { /* ignore */ }
}

// A team member's OWN Clerk session, forwarded so the otherwise-public portal endpoints can
// recognize "this is staff" (resolveStaffView, api/_shareDocs.js) and skip the PIN gate — mirrors
// services/journey.js's identical helper. Resolves to null for a real GC (no session) or if this
// page never set window.__clerkGetToken.
async function getOptionalStaffToken() {
  if (typeof window.__clerkGetToken !== 'function') return null
  try { return (await window.__clerkGetToken()) || null } catch { return null }
}
async function staffAuthHeader() {
  const t = await getOptionalStaffToken()
  return t ? { Authorization: 'Bearer ' + t } : {}
}

/**
 * Verify a portal token's PIN. Returns the signed proof on success (caller should cache it via
 * setCachedPinProof). Throws an Error with .code = 'pin_invalid' | 'locked' | other on failure;
 * a 'locked' error carries .retryAt.
 */
export async function verifyContractorPin(token, pin) {
  const r = await fetch('/api/contractor-pin', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ t: token, pin }),
  })
  const j = await r.json().catch(() => ({}))
  if (!r.ok) {
    const e = new Error(j.error || ('PIN check failed: ' + r.status))
    e.code = j.error || 'error'
    if (j.retryAt) e.retryAt = j.retryAt
    throw e
  }
  return j.proof
}

/**
 * Public, GC-facing: request a fresh PIN be emailed to THIS recipient's own on-file address
 * (never one supplied here). Only available for a per-recipient token; throws with
 * .code = 'reset_unavailable' for a legacy shared link, or .code = 'too_soon' if throttled.
 */
export async function requestContractorPinReset(token) {
  const r = await fetch('/api/contractor-pin', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ t: token, action: 'requestReset' }),
  })
  const j = await r.json().catch(() => ({}))
  if (!r.ok) {
    const e = new Error(j.error || ('Reset request failed: ' + r.status))
    e.code = j.error || 'error'
    throw e
  }
  return j
}

/**
 * Fetch a contractor portal by its token. Returns { contract, rfis, submittals, asis, drawings, ... }.
 * Throws a typed Error (.code) on: 'not_found', 'pin_required', 'pin_invalid', 'locked' (with
 * .retryAt) — callers show a PIN prompt for the pin_* codes rather than a generic error state.
 */
export async function getContractorPortal(token) {
  const proof = getCachedPinProof(token)
  const qs = new URLSearchParams({ t: token })
  if (proof) qs.set('pinProof', proof)
  const r = await fetch('/api/contractor-portal?' + qs.toString(), { headers: await staffAuthHeader() })
  const j = await r.json().catch(() => ({}))
  if (!r.ok) {
    const e = new Error(j.error || ('Contractor portal request failed: ' + r.status))
    e.code = j.error || (r.status === 404 ? 'not_found' : 'error')
    if (j.retryAt) e.retryAt = j.retryAt
    if (e.code === 'pin_invalid') setCachedPinProof(token, null)
    throw e
  }
  if (j.pinProof) setCachedPinProof(token, j.pinProof)
  return j
}

/* ── Public submit (token-gated; posts directly into the live record set) ── */

async function submitPost(token, action, body) {
  const r = await fetch('/api/contractor-submit', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await staffAuthHeader()) },
    body: JSON.stringify({ t: token, action, pinProof: getCachedPinProof(token), ...body }),
  })
  if (r.status === 404) { const e = new Error('not_found'); e.code = 'not_found'; throw e }
  const j = await r.json().catch(() => ({}))
  if (!r.ok) {
    const e = new Error(j.error || ('Contractor submit (' + action + ') failed: ' + r.status))
    e.code = j.error || 'error'
    throw e
  }
  return j
}

/** Upload one File to the contractor-uploads bucket; returns file metadata for a submission. */
export async function uploadPortalFile(token, file) {
  const { signedUrl, path, publicUrl } = await submitPost(token, 'getUploadUrl', {
    filename: file.name, contentType: file.type || 'application/octet-stream',
  })
  const put = await fetch(signedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream', 'x-upsert': 'true' },
    body: file,
  })
  if (!put.ok) throw new Error('Upload failed: ' + put.status)
  return { filename: file.name, url: publicUrl, storagePath: path, fileSize: file.size, fileType: null }
}

/** Submit an RFI — posts directly into the live RFI set. payload: { subject, question, submitted_by } */
export function submitRfi(token, payload, files = []) {
  return submitPost(token, 'submitRfi', { payload, files }).then(d => d.rfi)
}

/** Submit a Submittal — posts directly into the live Submittal set. payload: { spec_section_number, spec_section_name, gc_number, type, submitted_by } */
export function submitSubmittal(token, payload, files = []) {
  return submitPost(token, 'submitSubmittal', { payload, files }).then(d => d.submittal)
}

/** Submit a priced response to an architect proposal request — records directly on the PR. */
export function submitProposalResponse(token, proposalRequestId, payload, files = []) {
  return submitPost(token, 'submitProposalResponse', { proposalRequestId, payload, files }).then(d => d.response)
}

/** Subscribe an email to this contract's update notifications (idempotent). */
export function subscribeEmail(token, email) {
  return submitPost(token, 'subscribe', { email }).then(d => d.ok)
}

/** Upload one proof-of-fix photo File for a punch item into the punch-photos bucket (same bucket
 *  the team's own capture uses). Returns the stored path (append to flagPunchItemReady's photoPaths). */
export async function uploadPunchProofPhoto(token, itemId, file) {
  const { signedUrl, path } = await submitPost(token, 'getPunchPhotoUploadUrl', { itemId, filename: file.name })
  const res = await fetch(signedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream', 'x-upsert': 'true' },
    body: file,
  })
  if (!res.ok) throw new Error('Photo upload failed: ' + res.status)
  return path
}

/** Flag a punch item ready for review — the one status move the portal may make
 *  (GC_ALLOWED_TRANSITIONS, api/punch-items.js). Optional note + proof-of-fix photo paths. */
export function flagPunchItemReady(token, itemId, note, photoPaths = []) {
  return submitPost(token, 'flagPunchItemReady', { itemId, note, photoPaths }).then(d => d.item)
}

/** Download the issued spec or schedule PDF (token-gated; generated on demand). kind: 'spec'|'schedule'. */
export async function downloadDocPdf(token, kind, projectName) {
  const url = kind === 'schedule' ? '/api/schedule-pdf' : '/api/spec-pdf'
  const r = await fetch(url, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ t: token, projectName: projectName || '' }),
  })
  if (!r.ok) throw new Error('PDF request failed: ' + r.status)
  const blob = await r.blob()
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = (kind === 'schedule' ? 'Schedules' : 'Specifications') + '.pdf'
  document.body.appendChild(a); a.click(); a.remove()
  setTimeout(() => URL.revokeObjectURL(a.href), 4000)
}

/* ── Team admin (Clerk-authed) ───────────────────────────────────────────── */

async function adminPost(action, body) {
  const token = typeof window.__clerkGetToken === 'function'
    ? await window.__clerkGetToken().catch(() => null)
    : null
  const r = await fetch('/api/contractor-admin', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: 'Bearer ' + token } : {}),
    },
    body: JSON.stringify({ action, ...body }),
  })
  if (!r.ok) throw new Error('Contractor admin (' + action + ') failed: ' + r.status)
  return r.json()
}

/** Get (creating if absent) the portal for a contract. Returns { portal, link }. */
export function getOrCreatePortal(contractId) {
  return adminPost('getOrCreatePortal', { contractId })
}

/** Enable/disable a contract's portal. status: 'active' | 'disabled'. Returns { portal }. */
export function setPortalStatus(contractId, status) {
  return adminPost('setPortalStatus', { contractId, status })
}

/** Rotate the token (invalidates the old link). Returns { portal, link }. */
export function regenerateToken(contractId) {
  return adminPost('regenerateToken', { contractId })
}

/** List a contract's GC submissions (optionally filter by status). Returns { submissions }. */
export function listSubmissions(contractId, status) {
  return adminPost('listSubmissions', { contractId, status })
}

/** Mark a submission accepted, linking the created record. Called after the in-app modal creates it. */
export function acceptSubmission(submissionId, kind, recordId) {
  return adminPost('acceptSubmission', { submissionId, kind, recordId })
}

/** Reject a submission with an optional note. */
export function rejectSubmission(submissionId, note) {
  return adminPost('rejectSubmission', { submissionId, note })
}

/* ── Per-recipient access (2026-07) — mirrors services/journey.js's identical section ────────── */

/** List a portal's recipients (active + revoked). */
export function listRecipients(portalId) {
  return adminPost('listRecipients', { portalId }).then(d => d.recipients)
}

/** Add one or more recipients {name, email, contactId?} — creates + emails each their own link+PIN. */
export function addRecipients(portalId, recipients) {
  return adminPost('addRecipients', { portalId, recipients }).then(d => d.recipients)
}

/** Revoke/reactivate ONE recipient's access (doesn't affect anyone else on this portal). */
export function revokeRecipient(recipientId) {
  return adminPost('revokeRecipient', { recipientId }).then(d => d.recipient)
}
export function reactivateRecipient(recipientId) {
  return adminPost('reactivateRecipient', { recipientId }).then(d => d.recipient)
}

/** Permanently remove a revoked recipient row (e.g. added by mistake). */
export function deleteRecipient(recipientId) {
  return adminPost('deleteRecipient', { recipientId })
}

/** Resend a recipient's CURRENT link+PIN (no rotation). */
export function resendRecipientAccess(recipientId) {
  return adminPost('resendRecipientAccess', { recipientId })
}

/** Rotate ONE recipient's token+PIN (reactivating if revoked) and re-send. */
export function regenerateRecipientAccess(recipientId) {
  return adminPost('regenerateRecipientAccess', { recipientId }).then(d => d.recipient)
}
